<template>
  <div v-if="displayFilters">
    <span>
      <v-menu
        v-for="selectedFilter in selectedFilters"
        :key="selectedFilter.id"
        v-model="selectedFilter.isOpen"
        :close-on-content-click="false"
        dark
        offset-y
      >
        <template v-slot:activator="{ on }">
          <v-chip
            :id="`perso-filter-chip-${selectedFilter.id}`"
            dark
            class="device-filters-chip elevation-2 mr-3 my-2"
            :outlined="selectedFilter.active"
            close
            close-icon="mdi-close"
            v-on="on"
            @click:close="removeFilter(selectedFilter)"
          >
            <span>{{ $t('device.personalisation.filters.' + selectedFilter.translateKey) }}</span>
            <span v-if="selectedFilter.values && selectedFilter.values.length">
              <span v-if="$i18n.locale === 'fr'">&nbsp;:&nbsp;</span>
              <span v-else>:&nbsp;</span>
              <span v-if="selectedFilter.inputType === 'date'">{{ formatDateTime(selectedFilter.values) }}</span>
              <span v-else class="device-filters-chip-value" :class="{ inactive: !selectedFilter.active }">{{ getTranslateLabelSelectedOptions(selectedFilter) }}</span>
            </span>
          </v-chip>
        </template>
        <v-list class="pa-0 device-filters-list">
          <v-text-field
            v-if="selectedFilter.displayTextfield"
            :id="`perso-filter-query-${selectedFilter.id}`"
            v-model="selectedFilter.searchQuery"
            :label="$t('common.search')"
            single-line
            outlined
            dense
            hide-details
            :color="themeColors.secondary"
            class="pa-3"
            @keydown.enter="addValues(selectedFilter)"
            @input="formatValues(selectedFilter)"
          >
            <v-icon
              slot="append"
              :color="themeColors.secondary"
              :disabled="!selectedFilter.searchQuery.length"
              @click="addValues(selectedFilter)"
            >
              mdi-plus-circle-outline
            </v-icon>
          </v-text-field>
          <DatePicker
            v-else-if="selectedFilter.inputType === 'date'"
            :id="`perso-filter-date-${selectedFilter.id}`"
            v-model="selectedFilter.searchQuery"
            class="dat-version-date-field"
            format="DD/MM/YYYY HH:mm"
            :defaultPickerValue="getToday()"
            :showTime="{ format: 'HH:mm' }"
            :showToday="false"
            @ok="addValues(selectedFilter)"
          />
          <v-text-field
            v-else-if="selectedFilter.displaySearchBar"
            :id="`perso-filter-search-${selectedFilter.id}`"
            v-model="selectedFilter.searchQuery"
            :label="$t('common.search')"
            single-line
            outlined
            dense
            hide-details
            :color="themeColors.secondary"
            class="pa-3"
            @input="filterOptions(selectedFilter)"
          >
            <v-icon slot="append" :color="themeColors.secondary">mdi-magnify</v-icon>
          </v-text-field>
          <v-checkbox
            v-for="option in selectedFilter.options"
            :id="`perso-filter-${selectedFilter.id}-${option}`"
            :key="selectedFilter.id === 'countries' || selectedFilter.id === 'variantNames' ? option.name : option"
            v-model="selectedFilter.values"
            class="mx-3 my-4"
            hide-details="true"
            :value="getCheckboxValue(selectedFilter, option)"
            :label="getTranslateLabelOption(selectedFilter, option)"
            on-icon="mdi-checkbox-intermediate"
            :color="themeColors.secondary"
          />
        </v-list>
      </v-menu>
    </span>
    <v-menu dark offset-y>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          id="perso-add-filter"
          class="device-filters-btn colored"
          dark
          outlined
          rounded
          v-bind="attrs"
          v-on="on"
        >
          <v-icon dark small>mdi-plus</v-icon>
          {{ $t('device.filter.add') }}
        </v-btn>
      </template>
      <v-list class="pa-0 device-filters-list">
        <v-checkbox
          v-for="filter in filters"
          :id="`perso-filter-${filter.id}`"
          :key="filter.id"
          v-model="selectedFilters"
          class="mx-3 my-4"
          :value="filter"
          dark
          :label="$t('device.personalisation.filters.' + filter.translateKey)"
          on-icon="mdi-checkbox-intermediate"
          :color="themeColors.secondary"
          hide-details="true"
          @change="onChangeResetFilter(filter)"
        />
      </v-list>
    </v-menu>
    <div>
      <v-btn
        id="perso-apply-filter"
        class="device-filters-btn ctp-btn mr-4 px-8"
        dark
        rounded
        @click="applyFilter"
      >
        {{ $t('common.action.apply') }}
      </v-btn>
    </div>
    <v-divider dark class="mb-3" />
  </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import moment from 'moment';
import Utils from '@/mixins/Utils';
import { colors } from '@/constants/theme/colors';
import filtersPerso from '@/constants/components/filters/filtersPerso';
import { INavigation, store } from '@/store/modules/personalisation/types';
import { DatePicker } from 'ant-design-vue';
import { convertJSONFiltersToArrayFilters, filterToValue } from './utils';

import 'ant-design-vue/lib/date-picker/style/index.css';

@Component({
  components: {
    DatePicker
  }
})
export default class PersoFilters extends Mixins(Utils) {
  @Prop()
  private displayFilters!: boolean;

  @Prop()
  private filterConfig!: {[key: string]: string};

  @Prop()
  private numberOfElements!: number;

  private themeColors = colors;

  private activeFilterCount = 0;

  private dateRangePickerFormat = 'DD/MM/YYYY HH:mm';

  private timeDateRangeFormat = {
    format: 'HH:mm',
    defaultValue: [
      moment('00:00:00', 'HH:mm:ss'),
      moment('23:59:59', 'HH:mm:ss')
    ]
  };

  private getToday() {
    return moment().startOf('day');
  }

  private selectedFilters: { id: string, translateKey: string, active: boolean, options: string[], values: any, isOpen: boolean, displaySearchBar: boolean, inputType: string, searchQuery?: string }[] = [];

  private get filters() {
    return filtersPerso.map((filter: any) => filter);
  }

  @store.Action
  private updateNavigation!: (payload: INavigation) => Promise<void>;

  @store.Getter
  private getNavigationConfiguration!: INavigation;

  private reinitFilter(filter: any) {
    filter.active = false;
    filter.values = (typeof (filter.values) === 'string') ? '' : [];
    if (filter.searchQuery) {
      filter.searchQuery = '';
    }
    if (filter.id === 'deviceIds') {
      filter.options = [];
    }
    return filter;
  }

  @Watch('filterConfig')
  private updateSelectedFilters() {
    this.selectedFilters = [];
    this.filters.forEach(filter => this.reinitFilter(filter));
    if (this.filterConfig) {
      const filters = convertJSONFiltersToArrayFilters(this.filterConfig);
      filters.forEach((filter) => {
        this.addFilter(filter);
      });
      this.$emit('handleActiveFiltersCount', Object.keys(this.filterConfig).length);
    }
  }

  // Prop is set in case of redirection from deviceList
  @Watch('getNavigationConfiguration.start', { immediate: true })
  private handleRedirection() {
    if ((this.$route.params.deviceId) && (this.getNavigationConfiguration.start === 0)) {
      this.clearFilters();
      this.addFilter({ id: 'deviceIds', values: [this.$route.params.deviceId] });
      this.applyFilter();
    }
  }

  // Called from the parent PersonalisationToolbar
  public clearFilters() {
    this.filters.forEach((initialFilter) => {
      this.reinitFilter(initialFilter);
    });
    this.selectedFilters = [];
  }

  // Called when removing the chip in advanced filters
  private removeFilter(filter: { id: string, translateKey: string, options: string[] }) {
    this.filters.forEach((initialFilter) => {
      if (initialFilter.id === filter.id) {
        initialFilter = this.reinitFilter(initialFilter);
      }
      return initialFilter;
    });
    this.selectedFilters = this.selectedFilters.filter(element => element.id !== filter.id);
  }

  private addFilter(selectedfilter: { id: string, values: string[] | string }) {
    const filter = this.filters.find(initFilter => initFilter.id === selectedfilter.id);
    if (filter) {
      filter.values = selectedfilter.values;
      filter.active = true;
      filter.isOpen = false;
      if (filter.id === 'deviceIds') {
        filter.options = selectedfilter.values;
      }
      this.selectedFilters.push(filter);
    }
  }

  private onChangeResetFilter(filter: { id: string, translateKey: string, active: boolean, options: string[], values: string[] | string, isOpen: boolean, displaySearchBar: boolean }) {
    filter.isOpen = true;
    filter.active = false;
    filter.values = (typeof (filter.values) === 'string') ? '' : [];
  }

  private getTranslateLabelSelectedOptions(selectedFilter: any) {
    if (typeof (selectedFilter.values) === 'string') {
      return this.getTranslateLabelOption(selectedFilter, selectedFilter.values);
    }
    return selectedFilter.values.map((value: string) => this.getTranslateLabelOption(selectedFilter, value)).slice().sort().join(', ');
  }

  private getTranslateLabelOption(selectedFilter: any, option: string | { name: string, iso: string } | { name: string, id: number }) {
    let label = '';
    if (option != null && option !== '') {
      if (selectedFilter.id === 'device_status') {
        label = this.$t(`device.filter.option.${option}`).toString();
      } else if (selectedFilter.id === 'last_seen_seconds') {
        label = this.$t('device.filter.option.last_connection', {
          time: moment.duration(Number(option) * 1000).humanize()
        }).toString();
      } else if (selectedFilter.id === 'countries' && typeof option === 'object') {
        label = option.name;
      } else if (selectedFilter.id === 'variantNames' && typeof option === 'object') {
        label = option.name;
      } else {
        label = option.toString();
      }
    }
    if (option === '' && (selectedFilter.id === 'tollzone_codes' || selectedFilter.id === 'tollzone_names')) {
      label = this.$t('common.default').toString();
    }
    return label;
  }

  private applyFilter() {
    this.activeFilterCount = 0;
    const filters: {[key: string]: any} = {};
    const newNavigationConfiguration = {
      ...this.getNavigationConfiguration
    };
    // Remove advanced filters that were removed by clicking on the chip X
    // Diff between all possible advancedFilters && selectedFilters
    const allAdvancedFiltersId = this.filters.map(f => f.id);
    const selectedAdvancedFiltersId = this.selectedFilters.map(f => f.id);
    if (newNavigationConfiguration.filters) {
      Object.getOwnPropertyNames(newNavigationConfiguration.filters).forEach((prop) => {
      // If filter is an advanced filter but has been removed from the selectedFilters, remove it from the navigation filters
        if (allAdvancedFiltersId.includes(prop) && !selectedAdvancedFiltersId.includes(prop)) {
          delete newNavigationConfiguration.filters![prop as keyof typeof newNavigationConfiguration.filters];
        }
      });
    }
    this.selectedFilters.forEach((filter) => {
      if (filter.values && filter.values.length) {
        const filterValue = filterToValue(filter);
        filter.active = true;
        if (filter.searchQuery) {
          filter.searchQuery = '';
        }
        this.activeFilterCount++;
        filters[filter.id] = filterValue;
      }
    });
    // Use case, select incomplete top filter + deviceID advanced filter then clear advanced filters
    // Combine top filters & advanced filters if top filters exist
    if ((newNavigationConfiguration.filters && newNavigationConfiguration.filters.status && newNavigationConfiguration.filters.status.length > 0) || newNavigationConfiguration.filters?.isDeperso) {
      newNavigationConfiguration.filters = { ...newNavigationConfiguration.filters, ...filters };
    } else {
      // No top filters, apply only advanced filters
      newNavigationConfiguration.filters = filters;
    }
    newNavigationConfiguration.start = 0;
    this.updateNavigation({
      ...newNavigationConfiguration
    });
    this.$emit('handleActiveFiltersCount', this.activeFilterCount);
  }

  private filterOptions(selectedFilter: { id: string, translateKey: string, active: boolean, options: any[], values: string[] | string, isOpen: boolean, displaySearchBar: boolean, searchQuery: string }) {
    const initialFilters = this.filters.find(initFilter => initFilter.id === selectedFilter.id)?.initialOptions;
    let filtered: string[];
    if (initialFilters && selectedFilter.searchQuery.length > 0) {
      filtered = initialFilters.filter((option: any) => option.toString().toLowerCase().includes(selectedFilter.searchQuery.toLowerCase()));
      selectedFilter.options = [...filtered];
    } else if (initialFilters) {
      selectedFilter.options = [...initialFilters];
    }
  }

  private addValues(selectedFilter: { id: string, translateKey: string, active: boolean, options: string[], values: any[], isOpen: boolean, displaySearchBar: boolean, searchQuery: any }) {
    const oneValueOnly = ['createdDate', 'updatedDate'].includes(selectedFilter.id);
    if (oneValueOnly) {
      selectedFilter.values = [moment(selectedFilter.searchQuery).unix()];
    } else {
      const values = selectedFilter.searchQuery.includes(',') ? selectedFilter.searchQuery.split(',').map((item: string) => item.trim()) : selectedFilter.searchQuery;
      if (values && !oneValueOnly) {
        if (typeof values === 'string' && !selectedFilter.options.includes(values)) {
          selectedFilter.options.push(values);
          selectedFilter.values = [...selectedFilter.values, values];
        } else if (Array.isArray(values)) {
          values.forEach((value) => {
            if (!selectedFilter.options.includes(value)) {
              selectedFilter.options.push(value);
              selectedFilter.values = [...selectedFilter.values, value];
            }
          });
        }
      } else if (values && typeof values === 'string') {
        selectedFilter.values = [Number(values).toString()];
      }
    }
    selectedFilter.searchQuery = '';
  }

  private formatValues(selectedFilter: { id: string, translateKey: string, active: boolean, options: string[], values: string[] | string, isOpen: boolean, displaySearchBar: boolean, searchQuery: string }) {
    if (selectedFilter.searchQuery) {
      selectedFilter.searchQuery = selectedFilter.searchQuery.trim();
    }
  }

  getCheckboxValue(selectedFilter: any, option: any) {
    if (selectedFilter.id === 'countries') return option.name;
    if (selectedFilter.id === 'variantNames') return option;
    return option;
  }
}
</script>
<style lang="scss" scoped>
  @import './styles/perso-filters.scss';
</style>
